import { FcColorBlack, FcColorWhite } from "#lib/code.js";
import { LANDSCAPE_IMAGE_ASPECT_RATIO } from "#lib/components/images/constants.js";

export const BACKGROUND_REPEAT_OPTIONS = [
	{ name: "Tile image", value: "repeat" },
	{ name: "Tile horizontally", value: "repeat-x" },
	{ name: "Tile vertically", value: "repeat-y" },
	{ name: "Show once", value: "no-repeat" },
];

export const BACKGROUND_SIZE_OPTIONS = [
	{ name: "Fill area (crop if needed)", value: "cover" },
	{ name: "Fit entire image (no crop)", value: "contain" },
	{ name: "Actual size", value: "auto" },
];

export const BACKGROUND_POSITION_OPTIONS = [
	{ name: "Center", value: "center" },
	{ name: "Top left", value: "top left" },
	{ name: "Top", value: "top center" },
	{ name: "Top right", value: "top right" },
	{ name: "Left", value: "center left" },
	{ name: "Right", value: "center right" },
	{ name: "Bottom left", value: "bottom left" },
	{ name: "Bottom", value: "bottom center" },
	{ name: "Bottom right", value: "bottom right" },
];

export const DEFAULT_BACKGROUND_POSITIONING = { url: "", repeat: "no-repeat", size: "cover", position: "center", attachment: "scroll" };

export const BACKGROUND_ATTACHMENT_OPTIONS = [
	{ name: "Scroll", value: "scroll" },
	{ name: "Fixed", value: "fixed" },
];

export type TextElement = "h1" | "h2" | "p";

type AppearanceDefaults = {
	textSize: string;
	textAlignment: string;
	fontWeight: string;
};

export const TEXT_ELEMENTS = [
	{ name: "Paragraph", value: "p" },
	{ name: "Heading 1", value: "h1" },
	{ name: "Heading 2", value: "h2" },
];

export const HORIZONTAL_ALIGNMENTS = [
	{ name: "Left", value: "left" },
	{ name: "Center", value: "center" },
	{ name: "Right", value: "right" },
];

export const VERTICAL_ALIGNMENTS = [
	{ name: "Top", value: "top" },
	{ name: "Center", value: "center" },
	{ name: "Bottom", value: "bottom" },
];

export const VERTICAL_ALIGN_ITEMS: Record<string, string> = {
	top: "flex-start",
	center: "center",
	bottom: "flex-end",
};

export const HORIZONTAL_JUSTIFY: Record<string, string> = {
	left: "flex-start",
	center: "center",
	right: "flex-end",
};

export const BUTTON_SIZES = [
	{ name: "Small", value: "sm" },
	{ name: "Medium", value: "md" },
	{ name: "Large", value: "lg" },
	{ name: "Extra large", value: "xl" },
];

export const BUTTON_ICON_SIZE_CLASS: Record<string, string> = {
	sm: "h-4 w-4",
	md: "h-5 w-5",
	lg: "h-5 w-5",
	xl: "h-6 w-6",
};

export const BUTTON_ICON_CLEARANCE_CLASS: Record<string, { left: string; right: string }> = {
	sm: { left: "pl-6", right: "pr-6" },
	md: { left: "pl-7", right: "pr-7" },
	lg: { left: "pl-7", right: "pr-7" },
	xl: { left: "pl-8", right: "pr-8" },
};

export const BORDER_RADIUS_VALUES = [
	{ name: "None", value: "0" },
	{ name: "Small", value: "4px" },
	{ name: "Medium", value: "6px" },
	{ name: "Large", value: "8px" },
	{ name: "Extra large", value: "16px" },
];

export const FULL_BORDER_RADIUS = "calc(infinity * 1px)";

export const BUTTON_COLOR_DEFAULTS: Record<string, string> = {
	primaryButtonBackgroundColor: FcColorWhite,
	primaryButtonTextColor: FcColorBlack,
	borderColor: "#d1d5db",
	iconColor: "#9ca3af",
};

export const TEXT_SIZES = [
	{ name: "Small", value: "sm" },
	{ name: "Medium", value: "xl" },
	{ name: "Large", value: "3xl" },
];

export const FONT_WEIGHTS = [
	{ name: "Bold", value: "bold" },
	{ name: "Regular", value: "normal" },
];

export const ELEMENT_DEFAULTS: Record<TextElement, AppearanceDefaults> = {
	h1: {
		textSize: "3xl",
		textAlignment: "center",
		fontWeight: "bold",
	},
	h2: {
		textSize: "xl",
		textAlignment: "left",
		fontWeight: "bold",
	},
	p: {
		textSize: "sm",
		textAlignment: "left",
		fontWeight: "normal",
	},
};

export const BUTTON_DEFAULT = {
	borderRadius: "16px",
	textAlignment: "left",
	buttonSize: "xl",
	fontWeight: "bold",
	iconRightIconSet: {
		name: "chevron-right",
		iconSet: "heroicons",
	},
};

export const IMAGE_BUTTON_DEFAULT = {
	borderRadius: "6px",
	textAlignment: "left",
	backgroundColor: FcColorWhite,
	borderColor: "#e5e7eb",
	textColor: "#111928",
	textSize: "sm",
	aspectRatio: LANDSCAPE_IMAGE_ASPECT_RATIO,
	textShouldOverlay: false,
	overlayColor: "#00000000",
	verticalAlignment: "center",
	textBoxWidth: 100,
};

export const IMAGE_BUTTON_TEXT_LAYOUT: Record<string, { headerSize: string; subheaderSize: string; padding: string; paddingX: string; gap: string }> = {
	sm: { headerSize: "16px", subheaderSize: "14px", padding: "8px 16px", paddingX: "16px", gap: "4px" },
	xl: { headerSize: "20px", subheaderSize: "16px", padding: "12px 16px", paddingX: "16px", gap: "8px" },
	"3xl": { headerSize: "30px", subheaderSize: "20px", padding: "16px 16px", paddingX: "16px", gap: "8px" },
};

export const IMAGE_SIZES = [
	{ name: "Extra small", value: "xs" },
	{ name: "Small", value: "sm" },
	{ name: "Medium", value: "md" },
	{ name: "Large", value: "lg" },
];

export type BorderRadius = "none" | "sm" | "md" | "lg" | "full";

export const IMAGE_BORDER_RADIUS: { name: string; value: BorderRadius }[] = [
	{ name: "None", value: "none" },
	{ name: "Small", value: "sm" },
	{ name: "Medium", value: "md" },
	{ name: "Large", value: "lg" },
	{ name: "Full", value: "full" },
];

export const DEFAULT_HEADER_COMPONENT_TEXT = "Lorem ipsum dolor sit amet";
export const DEFAULT_PARAGRAPH_COMPONENT_TEXT = "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis pulvinar magna eu lectus porta faucibus.";
export const DEFAULT_BUTTON_COMPONENT_TEXT = "Enter text";
export const DEFAULT_VIDEO_SRC = "https://www.youtube.com/watch?v=T5WX63x5b60";
export const DEFAULT_POLL_ANSWER_IMAGE = { imageUrl: "/carousel-1.svg", altText: "Placeholder image" };

export const DEFAULT_FORM_STYLE = Object.freeze({
	"background-color": FcColorWhite,
	"border-color": "#d1d5db",
	"border-width": "1px",
	"border-style": "solid",
	"border-radius": "16px",
	padding: "12px",
});

type FormStyleControlType = "color" | "radius" | "spacing";

export type FormStyleControl = {
	styleKey: string;
	label: string;
	type: FormStyleControlType;
	default: string;
	tooltip?: string;
	allowTransparent?: boolean;
};

export const FORM_CONTAINER_CONTROLS: FormStyleControl[] = [
	{ styleKey: "background-color", label: "Background color", type: "color", default: FcColorWhite, allowTransparent: true },
	{ styleKey: "border-color", label: "Border color", type: "color", default: "#d1d5db", allowTransparent: true },
	{ styleKey: "border-radius", label: "Border radius", type: "radius", default: "16px" },
	{ styleKey: "padding", label: "Spacing", type: "spacing", default: "12px" },
];

// Colors for field label and description text, shown under Input appearance / Text elements.
export const FORM_TEXT_CONTROLS: FormStyleControl[] = [
	{ styleKey: "--form-label-color", label: "Label color", type: "color", default: "#111827", tooltip: "Color of each field's label." },
	{ styleKey: "--form-description-color", label: "Description color", type: "color", default: "#6b7280", tooltip: "Color of the secondary text under each field's title." },
];

// Standalone content elements, shown under the Content appearance section. Typography covers the static
// Header text element. Terms covers the terms & conditions legal text and its link.
export const FORM_TYPOGRAPHY_CONTROLS: FormStyleControl[] = [{ styleKey: "--form-header-color", label: "Heading color", type: "color", default: "#000000", tooltip: "Color of static Header text elements." }];

export const FORM_TERMS_CONTROLS: FormStyleControl[] = [
	{ styleKey: "--form-terms-color", label: "Text color", type: "color", default: "#111928", tooltip: "Color of terms & conditions legal text." },
	{ styleKey: "--form-terms-link-color", label: "Link color", type: "color", default: "#155dfc", tooltip: "Color of the terms & conditions link when it points to an external policy." },
];

export const FORM_INPUT_CONTROLS: FormStyleControl[] = [
	{ styleKey: "--form-input-text-color", label: "Text color", type: "color", default: "#111827" },
	{ styleKey: "--form-input-background-color", label: "Input background color", type: "color", default: "#f9fafb", allowTransparent: true },
	{ styleKey: "--form-input-border-color", label: "Border color", type: "color", default: "#d1d5db", allowTransparent: true },
	{ styleKey: "--form-input-border-radius", label: "Border radius", type: "radius", default: "8px" },
	{ styleKey: "--form-input-placeholder-color", label: "Placeholder color", type: "color", default: "#9ca3af" },
	{ styleKey: "--form-input-icon-color", label: "Icon color", type: "color", default: "#6b7280", tooltip: "Color of the dropdown chevron and date picker icon." },
	{ styleKey: "--form-input-checkbox-border-color", label: "Checkbox and Radio border color", type: "color", default: "#d1d5db" },
	{ styleKey: "--form-input-checkbox-background-color", label: "Checkbox and Radio background color", type: "color", default: "#f9fafb", allowTransparent: true },
];

export const FORM_SUBMIT_CONTROLS: FormStyleControl[] = [
	{ styleKey: "--form-submit-background-color", label: "Background color", type: "color", default: "#1f2a37", allowTransparent: true },
	{ styleKey: "--form-submit-border-color", label: "Border color", type: "color", default: "#1f2a37", allowTransparent: true },
	{ styleKey: "--form-submit-border-radius", label: "Border radius", type: "radius", default: "16px" },
	{ styleKey: "--form-submit-text-color", label: "Text color", type: "color", default: FcColorWhite },
];

export const FORM_MISC_CONTROLS: FormStyleControl[] = [
	{ styleKey: "--form-selected-color", label: "Selected color", type: "color", default: "#111928", tooltip: "Fill of selected checkboxes and radio buttons." },
	{ styleKey: "--form-error-color", label: "Error color", type: "color", default: "#e02424", tooltip: "Color of validation messages and invalid field outlines." },
];

export const FORM_SPACING_VALUES = [
	{ name: "None", value: "0" },
	{ name: "Small", value: "8px" },
	{ name: "Medium", value: "12px" },
	{ name: "Large", value: "16px" },
	{ name: "Extra large", value: "24px" },
];

export const DEFAULT_FORM_FIELDS = [
	{
		active: true,
		fieldType: "FIELD_TYPE_CHOICE",
		name: "choice",
		title: "Question 1",
		properties: {
			choice: {
				choiceType: "CHOICE_TYPE_DROPDOWN",
				choices: [{ value: "Option 1" }, { value: "Option 2" }],
			},
		},
	},
	{
		active: true,
		fieldType: "FIELD_TYPE_CHOICE",
		name: "choice_1",
		title: "Question 2",
		properties: {
			choice: {
				choiceType: "CHOICE_TYPE_CHECKBOX",
				choices: [{ value: "Option 1" }, { value: "Option 2" }],
			},
		},
	},
];

export const DEFAULT_POLL_QUESTIONS = [
	{
		name: "Poll 1",
		pollAnswers: [
			{ primaryLabel: "Option 1", image: { ...DEFAULT_POLL_ANSWER_IMAGE } },
			{ primaryLabel: "Option 2", image: { ...DEFAULT_POLL_ANSWER_IMAGE } },
		],
	},
	{
		name: "Poll 2",
		pollAnswers: [
			{ primaryLabel: "Option 1", image: { ...DEFAULT_POLL_ANSWER_IMAGE } },
			{ primaryLabel: "Option 2", image: { ...DEFAULT_POLL_ANSWER_IMAGE } },
		],
	},
];
